/**
 * Camera and microphone management module.
 *
 * Handles getUserMedia with spec-compliant constraints,
 * device enumeration with labels, track start/stop/replace,
 * and device switching (camera and microphone).
 */

/** Default video constraints: 720p ideal, user-facing camera */
const DEFAULT_VIDEO_CONSTRAINTS: MediaTrackConstraints = {
  facingMode: 'user',
  width: { ideal: 1280 },
  height: { ideal: 720 },
  frameRate: { ideal: 30 },
}

/** Default audio constraints with echo cancellation, noise suppression, auto gain */
const DEFAULT_AUDIO_CONSTRAINTS: MediaTrackConstraints = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
}

/** Categorized device lists returned by enumerateMediaDevices */
export interface DeviceLists {
  cameras: MediaDeviceInfo[]
  microphones: MediaDeviceInfo[]
  speakers: MediaDeviceInfo[]
}

/**
 * Acquire a media stream with spec-defined default constraints.
 * Callers can override individual constraint fields.
 */
export async function getMediaStream(constraints?: {
  video?: MediaTrackConstraints | boolean
  audio?: MediaTrackConstraints | boolean
}): Promise<MediaStream> {
  const videoConstraints =
    constraints?.video === false
      ? false
      : constraints?.video === true || constraints?.video === undefined
        ? DEFAULT_VIDEO_CONSTRAINTS
        : { ...DEFAULT_VIDEO_CONSTRAINTS, ...constraints.video }

  const audioConstraints =
    constraints?.audio === false
      ? false
      : constraints?.audio === true || constraints?.audio === undefined
        ? DEFAULT_AUDIO_CONSTRAINTS
        : { ...DEFAULT_AUDIO_CONSTRAINTS, ...constraints.audio }

  return navigator.mediaDevices.getUserMedia({
    video: videoConstraints,
    audio: audioConstraints,
  })
}

/**
 * Enumerate all available media devices, categorized by kind.
 * After getUserMedia permission is granted, labels will be populated.
 */
export async function enumerateMediaDevices(): Promise<DeviceLists> {
  const devices = await navigator.mediaDevices.enumerateDevices()
  return {
    cameras: devices.filter((d) => d.kind === 'videoinput'),
    microphones: devices.filter((d) => d.kind === 'audioinput'),
    speakers: devices.filter((d) => d.kind === 'audiooutput'),
  }
}

/**
 * Toggle audio mute on a stream by setting track.enabled.
 * Does not stop the track -- keeps the mic open for instant unmute.
 */
export function muteAudio(stream: MediaStream, muted: boolean): void {
  for (const track of stream.getAudioTracks()) {
    track.enabled = !muted
  }
}

/**
 * Toggle video mute on a stream by setting track.enabled.
 * Does not stop the track -- keeps the camera open for instant unmute.
 */
export function muteVideo(stream: MediaStream, muted: boolean): void {
  for (const track of stream.getVideoTracks()) {
    track.enabled = !muted
  }
}

/**
 * Switch to a different camera device.
 * Stops existing video tracks, acquires a new track with the specified deviceId,
 * and replaces it in the stream. Returns the new video track for sender replacement.
 */
export async function switchCamera(
  deviceId: string,
  currentStream: MediaStream,
): Promise<MediaStreamTrack> {
  // Stop current video tracks
  for (const track of currentStream.getVideoTracks()) {
    track.stop()
    currentStream.removeTrack(track)
  }

  // Acquire new video track with the specified device
  const newStream = await navigator.mediaDevices.getUserMedia({
    video: {
      deviceId: { exact: deviceId },
      width: { ideal: 1280 },
      height: { ideal: 720 },
      frameRate: { ideal: 30 },
    },
  })

  const newTrack = newStream.getVideoTracks()[0]
  currentStream.addTrack(newTrack)
  return newTrack
}

/**
 * Switch to a different microphone device.
 * Stops existing audio tracks, acquires a new track with the specified deviceId,
 * and replaces it in the stream. Returns the new audio track for sender replacement.
 */
export async function switchMicrophone(
  deviceId: string,
  currentStream: MediaStream,
): Promise<MediaStreamTrack> {
  // Stop current audio tracks
  for (const track of currentStream.getAudioTracks()) {
    track.stop()
    currentStream.removeTrack(track)
  }

  // Acquire new audio track with the specified device
  const newStream = await navigator.mediaDevices.getUserMedia({
    audio: {
      deviceId: { exact: deviceId },
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
    },
  })

  const newTrack = newStream.getAudioTracks()[0]
  currentStream.addTrack(newTrack)
  return newTrack
}

/**
 * Replace a track in all RTCPeerConnection senders.
 * Used after device switching to update the track without renegotiation.
 */
export async function replaceTrackInSenders(
  newTrack: MediaStreamTrack,
  peerConnections: RTCPeerConnection[],
): Promise<void> {
  const kind = newTrack.kind
  const replacements = peerConnections.flatMap((pc) =>
    pc
      .getSenders()
      .filter((sender) => sender.track?.kind === kind)
      .map((sender) => sender.replaceTrack(newTrack)),
  )
  await Promise.all(replacements)
}

/**
 * Stop all tracks in a stream, releasing camera/microphone access.
 */
export function stopAllTracks(stream: MediaStream): void {
  for (const track of stream.getTracks()) {
    track.stop()
  }
}
